// src/components/competitions/CompetitionCard.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getStatus = (comp) => {
  const now   = Date.now();
  const start = comp.start_date ? new Date(comp.start_date).getTime() : null;
  const end   = comp.end_date   ? new Date(comp.end_date).getTime()   : null;

  if (end && now > end)     return "ended";
  if (start && now < start) return "upcoming";
  return "live";
};

const formatCountdown = (targetMs) => {
  const diff = targetMs - Date.now();
  if (diff <= 0) return null;

  const totalSeconds = Math.floor(diff / 1000);
  const days    = Math.floor(totalSeconds / 86400);
  const hours   = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0)  return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
const StatusBadge = ({ status }) => {
  const styles = {
    live:     "bg-imperial-red text-white",
    upcoming: "border border-mikado-yellow/40 text-mikado-yellow bg-mikado-yellow/10",
    ended:    "border border-swan/20 text-swan/40 bg-transparent",
  };
  const labels = { live: "Live", upcoming: "Upcoming", ended: "Ended" };

  // Don't render a badge for ended competitions
  // if (status === "ended") return null;

  if (status === "ended") return (
  <span className="absolute top-2 right-2 text-[10px] font-bold tracking-widest uppercase px-2 py-[3px] rounded bg-samson-blue text-swan">
    Ended
  </span>
);

  return (
    <span
      className={`
        absolute top-2 right-2
        text-[10px] font-bold tracking-widest uppercase
        px-2 py-[3px] rounded
        ${styles[status]}
      `}
    >
      {labels[status]}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Timer label — contextual text based on status
// ---------------------------------------------------------------------------
const TimerLabel = ({ status, countdown }) => {

  if (status === "ended") return (
    <span className="text-xs text-swan/30">Ended</span>
  );

  if (!countdown) return (
    <span className="text-xs text-swan/30">—</span>
  );

  const prefix = status === "upcoming" ? "Starting in" : "Ending in";

  return (
    <span className="text-xs font-bold text-imperial-red tracking-wide font-mono">
      {prefix} {countdown}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Main card component
// ---------------------------------------------------------------------------
const CompetitionCard = ({ competition: comp }) => {
  const navigate = useNavigate();
  const [, setTick] = useState(0);

  // Tick every minute to keep countdowns fresh
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const status = getStatus(comp);


  const countdownTarget =
    status === "live"     ? (comp.end_date   ? new Date(comp.end_date).getTime()   : null) :
    status === "upcoming" ? (comp.start_date ? new Date(comp.start_date).getTime() : null) :
    null;

  const countdown = countdownTarget ? formatCountdown(countdownTarget) : null;

  const handleClick = () => {
    navigate(`/competition/${comp.id}`, { state: { competition: comp } });
  };

  return (
    <div
      onClick={handleClick}
      className="
        group relative cursor-pointer
        bg-pebble-brown rounded-xl overflow-hidden
        border border-white/5
        hover:border-white/10 hover:-translate-y-0.5
        transition-all duration-200
      "
    >
      {/* ---- Image ---- */}
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={comp.img_url}
          alt={comp.title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
        />
        <StatusBadge status={status} />
      </div>

      {/* ---- Body ---- */}
      <div className="p-4">

        {/* Title */}
        <h3 className="text-base font-bold text-imperial-red leading-snug mb-1 line-clamp-1">
          {comp.title}
        </h3>

        {/* Description */}
        <p className="text-xs text-swan/50 mb-3">
          {(() => {
            const text = comp.description ?? '';
            const sentences = text.split(/(?<=[.!?])\s+/).slice(0, 2).join(' ');
            return sentences.length < text.length ? sentences : text.slice(0, 60).trimEnd() + (text.length > 60 ? '…' : '');
          })()}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <TimerLabel status={status} countdown={countdown} />
          <span className="text-swan/20 group-hover:text-swan/50 transition-colors text-sm">
            →
          </span>
        </div>

      </div>
    </div>
  );
};

export default CompetitionCard;